import { useState } from "react";
import { motion } from "framer-motion";
import MagneticButton from "@/components/ui/MagneticButton";

export default function StudentHabitDemo() {
  const [consistency, setConsistency] = useState(30);

  // Calculate chaos based on consistency (Lower consistency = Higher chaos)
  const chaos = 100 - consistency;

  return (
    <div className="w-full max-w-md mx-auto p-6 rounded-3xl bg-[var(--glass-bg)] border border-[var(--glass-border)] backdrop-blur-xl shadow-2xl relative overflow-hidden transition-colors duration-700">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-teal-400/10 z-0" />

      <div className="relative z-10 space-y-6">
        <div className="text-center space-y-1">
          <h3 className="font-heading text-xl text-[var(--text-main)]">Student Habit Formation</h3>
          <p className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest">Nily360 Campus</p>
        </div>

        {/* The Habit Visualizer (Chaos to Order) */}
        <div className="h-40 relative flex items-center justify-center">
          {/* Central Core (The Habit) */}
          <motion.div
            className="w-16 h-16 rounded-full bg-[var(--nily-teal)] shadow-[0_0_30px_var(--nily-teal)] z-20 flex items-center justify-center text-[var(--bg-main)] font-bold text-xs"
            animate={{ scale: consistency > 80 ? 1.2 : 1 }}
          >
            {consistency > 80 ? "LOCKED" : "BUILDING"}
          </motion.div>

          {/* Orbiting Particles (Distractions) */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 rounded-full bg-orange-400"
              animate={{
                x: Math.cos(i) * (chaos * 1.5),
                y: Math.sin(i) * (chaos * 1.5),
                scale: chaos / 50,
                opacity: chaos / 100
              }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }}
            />
          ))}

          {/* Stabilizing Rings (The Support System) */}
          <motion.div
            className="absolute inset-0 rounded-full border border-[var(--nily-teal)]"
            animate={{ scale: [0.8, 1.2], opacity: consistency / 100 }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>

        {/* Interactive Slider */}
        <div className="space-y-3">
          <div className="flex justify-between text-xs text-[var(--text-muted)] font-medium">
            <span>Sporadic</span>
            <span>Consistent Routine</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={consistency}
            onChange={(e) => setConsistency(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none bg-[var(--text-muted)]/20 cursor-pointer accent-[var(--nily-teal)]"
          />
        </div>

        {/* Insight Card */}
        <div className="p-3 rounded-xl bg-[var(--bg-main)]/50 border border-[var(--glass-border)] text-center">
          <p className="text-sm text-[var(--text-main)]">
            {consistency < 50
              ? "High distraction risk. Need micro-nudges."
              : "Neuroplasticity activated. Habit creates stability."}
          </p>
        </div>
      </div>
    </div>
  );
}
